import { useQuery } from '@tanstack/react-query'
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore'

import { db } from '@/firebase'

interface CreditPrice {
  id: string
  unit_amount: number
  currency: string
  metadata?: {
    credit_amount?: string
    package_type?: string
    package_name?: string
  }
}

interface CreditProduct {
  id: string
  name: string
  description: string
  role: string
}

interface CreditPackagesResponse {
  product: CreditProduct | null
  prices: CreditPrice[]
}

const getCreditPackagesPrices = async (): Promise<CreditPackagesResponse> => {
  const PRODUCTS_COLLECTION = 'io_products'
  const CREDIT_TOPOFF_ROLE = 'credit_topoff'

  try {
    // Get the credit top-off product
    const productQuery = query(collection(db, PRODUCTS_COLLECTION), where('role', '==', CREDIT_TOPOFF_ROLE))

    const productResponse = await getDocs(productQuery)

    if (productResponse.empty) {
      console.warn('No credit top-off product found')

      return { product: null, prices: [] }
    }

    const productDoc = productResponse.docs[0]
    const productId = productDoc.id
    const productData = { id: productId, ...productDoc.data() } as CreditProduct

    // Get prices for the product, ordered by amount (ascending)
    const pricesQuery = query(collection(db, PRODUCTS_COLLECTION, productId, 'prices'), orderBy('unit_amount', 'asc'))

    const pricesResponse = await getDocs(pricesQuery)
    const pricesData: CreditPrice[] = pricesResponse.docs.map(priceDoc => ({
      id: priceDoc.id,
      ...priceDoc.data(),
    })) as CreditPrice[]

    return {
      product: productData,
      prices: pricesData,
    }
  } catch (error) {
    console.error('Error fetching credit packages prices:', error)

    return { product: null, prices: [] }
  }
}

export const useCreditPackagesPrices = () => {
  return useQuery({
    queryKey: ['creditPackagesPrices'],
    queryFn: getCreditPackagesPrices,
    retry: 2,
  })
}
